import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  TranslocoModule,
  TranslocoService,
  provideTranslocoScope,
} from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';
import { CredentialsMode } from '../../../collection-flow/target-runtime';
import { ConnectionEntity } from '../../../interfaces/collection.interface';
import {
  ResolvedCredentialField,
  ResolvedCredentialSchema,
  buildCredentialPayload,
  getUnresolvedOneOfControlNames,
  resolveCredentialFieldLabel,
  resolveCredentialSchema,
} from '../../../utils/credential-schema-resolver';
import {
  ConnectionAttemptBannerComponent,
  ConnectionAttemptBannerVariant,
} from '../../connection-attempt-banner/connection-attempt-banner.component';
import { ConnectionEntityBrandChipComponent } from '../../connection-entity-brand-chip/connection-entity-brand-chip.component';

@Component({
  selector: 'lib-credentials-step',
  imports: [
    CommonModule,
    TranslocoModule,
    ReactiveFormsModule,
    MzicSvgComponent,
    SpinnerComponent,
    ConnectionAttemptBannerComponent,
    ConnectionEntityBrandChipComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './credentials-step.component.html',
  styleUrl: './credentials-step.component.scss',
})
export class CredentialsStepComponent implements OnInit {
  private readonly translocoService = inject(TranslocoService);
  private resolvedSchema: ResolvedCredentialSchema = {
    fields: [],
    baseRequiredSourceFields: [],
  };

  @Input() collectionId = '';
  @Input() targetId = '';
  @Input() connectionEntity!: ConnectionEntity;
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() mode: CredentialsMode = 'initial';
  @Input() failureMessage: string | null = null;
  @Input() failureCode: string | null = null;
  @Input() failureOccurredAt: string | null = null;
  @Output() credentialsSubmitted = new EventEmitter<Record<string, string>>();
  @Output() back = new EventEmitter<void>();

  loading = signal(false);
  showPasswordFields = signal<Record<string, boolean>>({});

  credentialsForm = new FormGroup({});

  ngOnInit(): void {
    this.buildForm();
  }

  get credentialFields(): ResolvedCredentialField[] {
    return this.resolvedSchema.fields;
  }

  get entityDisplayName(): string {
    return this.connectionEntity?.display_name || '';
  }

  /** Front-owned copy for codes whose backend message is not user-facing. */
  get failureMessageKey(): string | null {
    switch (this.failureCode) {
      case 'TOO_MANY_ATTEMPTS':
        return 'lang.credentials_failure_too_many_attempts';
      case 'AUTH_INTERACTION_EXPIRED':
        return 'lang.credentials_failure_auth_interaction_expired';
      case 'AUTH_INTERACTION_REJECTED':
        return 'lang.credentials_failure_auth_interaction_rejected';
      case 'AUTH_INTERACTION_FAILED':
        return 'lang.credentials_failure_auth_interaction_failed';
      default:
        return null;
    }
  }

  get integrationSuffix(): string {
    return this.integrationEnabled ? '_integration' : '';
  }

  get credentialsDescriptionKey(): string {
    switch (this.mode) {
      case 'reauth':
        return `lang.credentials_description_reauth${this.integrationSuffix}`;
      case 'failed_retry':
        return `lang.credentials_description_failed_retry${this.integrationSuffix}`;
      default:
        return `lang.credentials_description${this.integrationSuffix}`;
    }
  }

  get credentialsTitleKey(): string {
    switch (this.mode) {
      case 'reauth':
        return 'lang.credentials_title_reauth';
      case 'failed_retry':
        return 'lang.credentials_title_failed_retry';
      default:
        return 'lang.credentials_title';
    }
  }

  get credentialsInfoNoteKey(): string {
    return `lang.credentials_info_note${this.integrationSuffix}`;
  }

  get submitLabelKey(): string {
    switch (this.mode) {
      case 'reauth':
        return 'lang.credentials_submit_reauth';
      case 'failed_retry':
        return 'lang.credentials_submit_failed_retry';
      default:
        return 'lang.credentials_submit';
    }
  }

  get bannerVariant(): ConnectionAttemptBannerVariant | null {
    if (this.failureMessage) {
      return 'failed-retry';
    }

    switch (this.mode) {
      case 'reauth':
        return 'reauth-credentials';
      case 'failed_retry':
        return 'failed-retry';
      default:
        return null;
    }
  }

  get shouldShowFailureMetadata(): boolean {
    return (
      !!this.failureMessage &&
      (!!this.failureCode || !!this.formattedFailureOccurredAt)
    );
  }

  get formattedFailureOccurredAt(): string | null {
    if (!this.failureOccurredAt) {
      return null;
    }

    const timestamp = Date.parse(this.failureOccurredAt);
    if (Number.isNaN(timestamp)) {
      return null;
    }

    const locale = this.translocoService.getActiveLang().startsWith('pt')
      ? 'pt-BR'
      : 'en-US';

    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(timestamp));
  }

  get entityIconUrl(): string | null {
    return this.connectionEntity?.icon_url || null;
  }

  hasError(controlName: string): boolean {
    const control = this.credentialsForm.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  isPasswordField(field: ResolvedCredentialField): boolean {
    return field.semantic === 'password';
  }

  isFieldRequired(field: ResolvedCredentialField): boolean {
    return field.required;
  }

  togglePasswordVisibility(controlName: string): void {
    const current = this.showPasswordFields();
    this.showPasswordFields.set({
      ...current,
      [controlName]: !current[controlName],
    });
  }

  isPasswordVisible(controlName: string): boolean {
    return this.showPasswordFields()[controlName] || false;
  }

  getFieldType(field: ResolvedCredentialField): string {
    if (field.semantic === 'password') {
      return this.isPasswordVisible(field.controlName) ? 'text' : 'password';
    }

    return field.inputType;
  }

  getFieldLabel(field: ResolvedCredentialField): string {
    return resolveCredentialFieldLabel(field, (key) =>
      this.translocoService.translate(`lang.${key}`),
    );
  }

  getFieldPlaceholder(field: ResolvedCredentialField): string {
    if (field.placeholderKey) {
      return this.translocoService.translate(`lang.${field.placeholderKey}`);
    }

    const label = this.getFieldLabel(field);
    return `Enter your ${label.toLowerCase()}`;
  }

  getFieldError(field: ResolvedCredentialField): string {
    if (field.errorKey) {
      return this.translocoService.translate(`lang.${field.errorKey}`);
    }

    return `${this.getFieldLabel(field)} is required`;
  }

  onFieldInput(field: ResolvedCredentialField, event: Event): void {
    if (!field.formatOnInput) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const formattedValue = field.formatOnInput(input.value);
    const control = this.credentialsForm.get(field.controlName);

    if (!control || control.value === formattedValue) {
      input.value = formattedValue;
      return;
    }

    control.setValue(formattedValue);
    input.value = formattedValue;
  }

  onSubmit(): void {
    const invalidOneOfControls = this.validateOneOfSelection();
    if (invalidOneOfControls) {
      this.markFieldsAsTouched(invalidOneOfControls);
      return;
    }

    if (this.credentialsForm.valid) {
      const values = this.credentialsForm.getRawValue() as Record<
        string,
        string
      >;
      const credentials = buildCredentialPayload(this.resolvedSchema, values);
      this.loading.set(true);
      this.credentialsSubmitted.emit(credentials);
      return;
    }

    this.markFieldsAsTouched(Object.keys(this.credentialsForm.controls));
  }

  onBack(): void {
    this.back.emit();
  }

  resetLoading(): void {
    this.loading.set(false);
  }

  beginLoading(): void {
    this.loading.set(true);
  }

  private buildForm(): void {
    const controls: Record<string, FormControl> = {};
    const passwordVisibility: Record<string, boolean> = {};
    const resolvedSchema = resolveCredentialSchema(
      this.connectionEntity.credential_schema,
    );
    this.resolvedSchema = resolvedSchema;

    resolvedSchema.fields.forEach((field) => {
      const validators = field.required
        ? [Validators.required, ...field.validators]
        : [...field.validators];

      controls[field.controlName] = new FormControl('', validators);

      if (field.semantic === 'password') {
        passwordVisibility[field.controlName] = false;
      }
    });

    this.credentialsForm = new FormGroup(controls);
    this.showPasswordFields.set(passwordVisibility);
  }

  private validateOneOfSelection(): string[] | null {
    const values = this.credentialsForm.getRawValue() as Record<string, string>;
    const invalidControls = getUnresolvedOneOfControlNames(
      this.resolvedSchema,
      values,
    );
    const oneOfControlNames =
      this.resolvedSchema.oneOf?.type === 'separate'
        ? this.resolvedSchema.oneOf.controlNames
        : [];

    if (!invalidControls) {
      this.clearOneOfErrors(oneOfControlNames);
      return null;
    }

    this.applyOneOfErrors(invalidControls, values);
    return invalidControls.length > 0 ? invalidControls : null;
  }

  private applyOneOfErrors(
    controlNames: string[],
    values: Record<string, string>,
  ): void {
    controlNames.forEach((controlName) => {
      const control = this.credentialsForm.get(controlName);
      if (!control) {
        return;
      }

      const hasValue = (values[controlName] || '').trim().length > 0;
      if (!hasValue) {
        const errors = { ...(control.errors ?? {}), oneOfRequired: true };
        control.setErrors(errors);
      }
    });
  }

  private clearOneOfErrors(controlNames: string[]): void {
    controlNames.forEach((controlName) => {
      const control = this.credentialsForm.get(controlName);
      if (!control?.errors) {
        return;
      }

      const nextErrors = { ...control.errors };
      delete nextErrors['oneOfRequired'];
      control.setErrors(Object.keys(nextErrors).length > 0 ? nextErrors : null);
    });
  }

  private markFieldsAsTouched(controlNames: string[]): void {
    controlNames.forEach((controlName) => {
      this.credentialsForm.get(controlName)?.markAsTouched();
    });
  }
}
